import { ReactElement, useEffect, useMemo, useState } from 'react';
import { debounce, isEqual } from 'lodash';
import { UseFormReturn } from 'react-hook-form';

import { useImmutableCallback } from '@agro1desenvolvimento/react-hooks';
import { StepComponent } from '../types';
import { validateSchemaSync } from '../../../../utils/yup';
import { RecomendacaoSemeaduraFormValues } from '../../../../@types';

export type Tab = {
  key: string;
  label: string;
  disabled?: boolean;
  isValid?: boolean;
  Component: StepComponent;
  icon?: string | ReactElement;
};

const useTabs = (
  form: UseFormReturn<RecomendacaoSemeaduraFormValues>,
  defaultTabs: Tab[],
) => {
  const [tabs, setTabs] = useState<Tab[]>(() => [...defaultTabs]);
  const { watch } = form;

  const updateTabsValidation = useImmutableCallback(() => {
    setTabs((oldTabs) => {
      const newTabs: Tab[] = [];
      const values = form.getValues();

      oldTabs.forEach((step, index) => {
        const prevTab = index === 0 ? undefined : newTabs[index - 1];

        let disabled = false;

        if (prevTab) {
          disabled =
            prevTab.disabled ||
            !validateSchemaSync(prevTab.Component.validationSchema, values);
        }

        const isValid = validateSchemaSync(
          step.Component.validationSchema,
          values,
        );

        newTabs[index] = { ...step, disabled, isValid };
      });

      if (isEqual(newTabs, oldTabs)) {
        return oldTabs;
      }

      return newTabs;
    });
  });

  const debouncedUpdatedTabsValidation = useMemo(
    () => debounce(updateTabsValidation, 50),
    [updateTabsValidation],
  );

  useEffect(() => {
    const subscription = watch(debouncedUpdatedTabsValidation);

    return () => subscription.unsubscribe();
  }, [debouncedUpdatedTabsValidation, watch]);

  return tabs;
};

export default useTabs;
